import { sendFunnelEvent } from "~/api/funnel";
import type {
  AppointmentData,
  CalendarEventData,
  FunnelEvent,
  FunnelEventType,
  FunnelStep,
  ProviderFilters,
  ProviderLoadedInfo,
  ProvidersLoadedTrigger,
  StepSelections,
  UtmParams,
  ValidationErrorMeta,
} from "~/shared/utils/types";
import { useFunnelStore } from "~/stores/useFunnelStore";
import { useSignUpStore } from "~/stores/useSignUpStore";
import { getUtmParams } from "../utils/utmCapture";
import { FLOW_VERSION } from "./flowVersion";

/**
 * Module-level flow version override.
 * When set, sendEvent uses this instead of the default FLOW_VERSION.
 * Allows admin/backoffice screens to tag events with a distinct version.
 */
let flowVersionOverride: string | null = null;

/**
 * Get UTM params from store with fallback
 */
const getUtms = (): UtmParams | undefined => {
  // Try Zustand store first
  const storeUtms = useSignUpStore.getState().patient.utmParams;
  if (storeUtms && Object.keys(storeUtms).length > 0) {
    return storeUtms;
  }

  // Fallback to utmCapture memory/sessionStorage
  const capturedUtms = getUtmParams();
  return capturedUtms ?? undefined;
};

/**
 * Calculate duration since step started
 */
const getStepDuration = (): number | undefined => {
  const stepStartedAt = useFunnelStore.getState().step_started_at;
  if (!stepStartedAt) return undefined;

  const startedAt = new Date(stepStartedAt).getTime();
  return Date.now() - startedAt;
};

/**
 * Build and send a funnel event
 */
const sendEvent = async (
  eventType: FunnelEventType,
  step: FunnelStep,
  metadata?: {
    validation_errors?: ValidationErrorMeta[];
    selections?: StepSelections;
    calendar?: CalendarEventData;
    appointment?: AppointmentData;
  },
): Promise<void> => {
  const store = useFunnelStore.getState();
  const sessionId = store.getOrCreateSession();

  const event: FunnelEvent = {
    session_id: sessionId,
    healthie_id: store.healthie_id ?? undefined,
    event_type: eventType,
    step,
    timestamp: new Date().toISOString(),
    duration_ms: getStepDuration(),
    metadata: {
      ...metadata,
      utm: getUtms(),
      user_agent:
        typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      referrer: typeof document !== "undefined" ? document.referrer : undefined,
      flow_version: flowVersionOverride ?? FLOW_VERSION,
      step_position: store.step_position,
    },
  };

  if (process.env.NODE_ENV === "development") {
    console.log("[Funnel]", event.event_type, event.step, event);
  }

  await sendFunnelEvent(event);
};

/**
 * Track funnel start (user clicks "Start")
 */
export const trackFunnelStart = async (): Promise<void> => {
  const store = useFunnelStore.getState();

  if (store.isSessionExpired()) {
    store.resetSession();
  }

  startStep("landing");
  await sendEvent("funnel_start", "landing");
};

/**
 * Mark step as started (for duration tracking)
 */
export const startStep = (step: FunnelStep): void => {
  useFunnelStore.getState().startStep(step);
};

/**
 * Get current session (read-only)
 */
export const getSession = () => {
  const store = useFunnelStore.getState();
  return {
    session_id: store.getOrCreateSession(),
    healthie_id: store.healthie_id,
    current_step: store.current_step,
    started_at: store.started_at,
  };
};

/**
 * Set the Healthie ID on the session
 */
export const setHealthieId = (healthieId: string): void => {
  useFunnelStore.getState().setHealthieId(healthieId);
};

/**
 * Get the Healthie ID from session
 */
export const getHealthieId = (): string | undefined => {
  return useFunnelStore.getState().healthie_id ?? undefined;
};

/**
 * Track step completion with optional selection data
 */
export const trackStepComplete = async (
  step: FunnelStep,
  selections?: StepSelections,
): Promise<void> => {
  await sendEvent("step_complete", step, { selections });
};

/**
 * Track back navigation
 */
export const trackStepBack = async (fromStep: FunnelStep): Promise<void> => {
  await sendEvent("step_back", fromStep);
};

/**
 * Track validation error
 */
export const trackValidationError = async (
  step: FunnelStep,
  errors: ValidationErrorMeta[],
): Promise<void> => {
  await sendEvent("validation_error", step, { validation_errors: errors });
};

/**
 * Track funnel completion (appointment booked) with appointment details
 */
export const trackFunnelComplete = async (
  appointment?: AppointmentData,
): Promise<void> => {
  await sendEvent("funnel_complete", "appointment_complete", { appointment });
};

// ============================================
// CALENDAR & PROVIDER SELECTION TRACKING
// ============================================

/**
 * Track calendar navigation (prev/next week)
 */
export const trackCalendarNavigate = async (
  direction: "prev" | "next",
): Promise<void> => {
  await sendEvent("calendar_navigate", "provider_selection", {
    calendar: { direction },
  });
};

/**
 * Track date selection on calendar
 */
export const trackCalendarDateSelect = async (
  selectedDate: string,
  previousDate?: string,
  appointmentType?: "telehealth" | "in-person",
): Promise<void> => {
  await sendEvent("calendar_date_select", "provider_selection", {
    calendar: {
      selected_date: selectedDate,
      previous_date: previousDate,
      appointment_type: appointmentType,
    },
  });
};

/**
 * Track when providers are loaded with full context
 */
export const trackProvidersLoaded = async (
  providers: ProviderLoadedInfo[],
  filters: ProviderFilters,
  trigger: ProvidersLoadedTrigger,
): Promise<void> => {
  const totalSlots = providers.reduce((sum, p) => sum + p.slots, 0);

  await sendEvent("providers_loaded", "provider_selection", {
    calendar: {
      provider_count: providers.length,
      total_slots: totalSlots,
      selected_date: filters.date,
      appointment_type: filters.appointment_type,
      trigger,
      providers,
      filters,
    },
  });
};

/**
 * Track time slot selection
 */
export const trackTimeSlotSelect = async (
  providerId: string,
  providerName: string,
  selectedTime: string,
  slotsAvailable: number,
  slotLocation?: string,
): Promise<void> => {
  await sendEvent("time_slot_select", "provider_selection", {
    calendar: {
      provider_id: providerId,
      provider_name: providerName,
      selected_time: selectedTime,
      total_slots: slotsAvailable,
      slot_location: slotLocation,
    },
  });
};

/**
 * Track filter changes (specialty or location)
 */
export const trackFilterChange = async (
  filterType: "specialty" | "location",
  filterValue: string | string[],
): Promise<void> => {
  await sendEvent("filter_change", "provider_selection", {
    calendar: {
      filter_type: filterType,
      filter_value: filterValue,
    },
  });
};

/**
 * Track per-provider lookahead navigation (admin calendar)
 */
export const trackLookaheadNavigate = async (
  targetDate: string,
  targetProviderId: string,
): Promise<void> => {
  await sendEvent("lookahead_navigate", "provider_selection", {
    calendar: {
      target_date: targetDate,
      target_provider_id: targetProviderId,
    },
  });
};

export const trackAutoSwitchTelehealth = async (
  previousLocation: string,
  reason: "no_availability" | "no_providers",
): Promise<void> => {
  await sendEvent("auto_switch_telehealth", "provider_selection", {
    calendar: {
      previous_location: previousLocation,
      new_location: "Healthie Video Call",
      auto_switch_reason: reason,
    },
  });
};

export const trackPatientCreated = async (
  healthieId: string,
): Promise<void> => {
  await sendEvent("patient_created", "other_info", {
    selections: {
      healthie_id: healthieId,
    },
  });
};

export const trackPageView = async (): Promise<void> => {
  await sendEvent("page_view", "landing");
};

/**
 * Reset session (for testing or manual reset)
 */
export const resetSession = (): void => {
  useFunnelStore.getState().resetSession();
};

/**
 * Override the flow version for the current session.
 * Use for admin/backoffice screens to distinguish from patient flow.
 */
export const setFlowVersion = (version: string): void => {
  flowVersionOverride = version;
};

/**
 * Clear flow version override (revert to default).
 */
export const clearFlowVersion = (): void => {
  flowVersionOverride = null;
};

// Export all tracking functions as a namespace for convenience
export const funnelTracker = {
  getSession,
  setHealthieId,
  getHealthieId,
  startStep,
  trackFunnelStart,
  trackStepComplete,
  trackStepBack,
  trackValidationError,
  trackFunnelComplete,
  trackPatientCreated,
  trackPageView,
  // Calendar tracking
  trackCalendarNavigate,
  trackCalendarDateSelect,
  trackProvidersLoaded,
  trackTimeSlotSelect,
  trackFilterChange,
  trackAutoSwitchTelehealth,
  trackLookaheadNavigate,
  resetSession,
  // Flow version
  setFlowVersion,
  clearFlowVersion,
};

export default funnelTracker;
